import { type ActionEntry } from '@soulhx/fs-common';
import { db } from '../model/db.js';
import { DateTime } from 'luxon';

export const getActionEntriesFromDexie = async (fromDate?: string, toDate?: string): Promise<ActionEntry[]> => {
  const fromObj = fromDate ? DateTime.fromISO(fromDate) : undefined;
  const toObj = toDate ? DateTime.fromISO(toDate) : undefined;

  // entryDate is stored as an ISO string with the creator's local UTC
  // offset, so a plain string bound isn't exact across DST/timezone
  // differences. Pad the indexed range by a day on each side to stay
  // correct, then narrow with a precise DateTime comparison below.
  let collection: ReturnType<typeof db.actionEntries.toCollection>;
  if (fromObj && toObj) {
    collection = db.actionEntries
      .where('entryDate')
      .between(fromObj.minus({ day: 1 }).toISODate()!, toObj.plus({ day: 1 }).toISODate()!, true, true);
  } else if (fromObj) {
    collection = db.actionEntries.where('entryDate').aboveOrEqual(fromObj.minus({ day: 1 }).toISODate()!);
  } else if (toObj) {
    collection = db.actionEntries.where('entryDate').belowOrEqual(toObj.plus({ day: 1 }).toISODate()!);
  } else {
    collection = db.actionEntries.toCollection();
  }

  // rows queued for deletion are treated as already gone
  collection = collection.and((item) => item.syncStatus !== 'pendingDelete');
  if (fromObj) {
    collection = collection.and((item) => DateTime.fromISO(item.entryDate) >= fromObj);
  }
  if (toObj) {
    collection = collection.and((item) => DateTime.fromISO(item.entryDate) <= toObj);
  }

  const rawEntries = await collection.toArray();
  const typeIds = [...new Set(rawEntries.map((e) => e.actionType))];
  const types = await db.actionTypes.where('id').anyOf(typeIds).toArray();
  const typesById = new Map(types.map((t) => [t.id, t]));

  const entries: ActionEntry[] = rawEntries.map((re) => ({
    ...re,
    displayName: typesById.get(re.actionType)?.displayName ?? '',
  }));

  return entries;
};
